import { cn } from "@/lib/utils";
import type { AssistantConversationListItem } from "../../types";
import { partitionConversations } from "./conversation-partition";
import { ConversationRow, type ConversationRowDensity } from "./conversation-row";

/**
 * The one conversation list. The nav flyout renders it at `default` density and the panel's
 * Conversations tab at `dense`; nothing else about them differs, so there is one list to learn
 * rather than two.
 *
 * Grouping is **Active** (working or needs-you) then **Recent** (everything else), each in the
 * backend's own order — see `partitionConversations`. A group with no rows is not drawn at all,
 * label included; a label over nothing is a promise the list is not keeping.
 */
export function ConversationList({
  items,
  currentThreadId,
  density = "default",
  onSelect,
  emptyLabel = "No conversations yet",
  className,
}: {
  items: readonly AssistantConversationListItem[];
  currentThreadId: string | null;
  density?: ConversationRowDensity;
  onSelect: (threadId: string) => void;
  emptyLabel?: string;
  className?: string;
}) {
  const { active, recent } = partitionConversations(items);
  // One clock for the whole list, so two rows a second apart cannot disagree about "now".
  const now = Date.now();

  if (active.length === 0 && recent.length === 0) {
    return (
      <div className={cn("px-3 py-6 text-center text-xs text-muted-foreground", className)}>
        {emptyLabel}
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col", className)}>
      <ConversationGroup
        label="Active"
        items={active}
        currentThreadId={currentThreadId}
        density={density}
        onSelect={onSelect}
        now={now}
      />
      <ConversationGroup
        label="Recent"
        items={recent}
        currentThreadId={currentThreadId}
        density={density}
        onSelect={onSelect}
        now={now}
      />
    </div>
  );
}

/**
 * Group labels are quiet text on no background — they separate, they do not decorate. Grey is
 * spent on hover alone in this vocabulary, so a filled group header would read as a row.
 */
function ConversationGroup({
  label,
  items,
  currentThreadId,
  density,
  onSelect,
  now,
}: {
  label: string;
  items: readonly AssistantConversationListItem[];
  currentThreadId: string | null;
  density: ConversationRowDensity;
  onSelect: (threadId: string) => void;
  now: number;
}) {
  if (items.length === 0) {
    return null;
  }

  return (
    <section className="flex flex-col">
      <h3
        className={cn(
          "px-3 pb-1 text-[11px] font-normal text-muted-foreground",
          density === "dense" ? "pt-2.5" : "pt-3"
        )}
      >
        {label}
      </h3>
      {items.map((item) => (
        <ConversationRow
          key={item.threadId}
          item={item}
          isCurrent={item.threadId === currentThreadId}
          density={density}
          onSelect={onSelect}
          now={now}
        />
      ))}
    </section>
  );
}
